import React from 'react';
import { Comment, CommentsProgress } from '../../types';

interface CommentsViewProps {
  pendingComments: Comment[];
  commentsLoading: boolean;
  commentsSearchCompleted: boolean;
  commentsProgress: CommentsProgress;
  onRefreshComments: () => void;
}

const CommentsView: React.FC<CommentsViewProps> = ({
  pendingComments,
  commentsLoading,
  commentsSearchCompleted,
  commentsProgress,
  onRefreshComments,
}) => {
  const unrepliedComments = pendingComments.filter(comment => !comment.is_replied);

  return (
    <div className="comments-view">
      <div className="comments-header">
        <div className="comments-title-section">
          <h2>Pending Comments ({unrepliedComments.length})</h2>
          <button 
            className="refresh-comments-button"
            onClick={onRefreshComments}
            disabled={commentsLoading}
          >
            {commentsLoading ? 'Searching...' : 'Refresh Comments'}
          </button>
        </div>
        <p className="comments-description">
          Comments and mentions from your assigned tasks that need your attention.
        </p>
      </div>

      {commentsLoading && (
        <div className="loading-comments">
          <div className="search-status">
            <h3>🔍 Searching for Comments</h3>
            <p className="search-description">
              Scanning through all your projects and tasks to find pending comments and mentions...
            </p>
          </div>
          
          <div className="progress-section">
            <div className="progress-header">
              <div className="loading-detail">
                {commentsProgress.message}
              </div>
            </div>
            
            {commentsProgress.total > 0 && (
              <>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(commentsProgress.current / commentsProgress.total) * 100}%` }}
                  />
                </div>
                <p className="progress-text">
                  <span className="progress-percentage">
                    {Math.round((commentsProgress.current / commentsProgress.total) * 100)}%
                  </span>
                  {' '}complete ({commentsProgress.current} of {commentsProgress.total} projects)
                </p>
                
                {commentsProgress.currentProject && (
                  <div className="progress-details">
                    <div className="current-project">
                      <h4>Current Project</h4>
                      <p className="project-name">{commentsProgress.currentProject}</p>
                      <p className="project-stats">
                        {commentsProgress.tasksInProject} tasks to check
                      </p>
                    </div>
                    
                    {commentsProgress.currentTask && (
                      <div className="current-task">
                        <h4>Current Task</h4>
                        <p className="task-name">{commentsProgress.currentTask}</p>
                        <p className="task-progress">
                          Task {commentsProgress.currentTaskIndex} of {commentsProgress.tasksInProject}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {!commentsLoading && commentsSearchCompleted && (
        <>
          {unrepliedComments.length > 0 ? (
            <div className="comments-list">
              {unrepliedComments.map((comment) => (
                <div key={comment.gid} className="comment-item">
                  <div className="comment-header">
                    <div className="comment-meta">
                      <span className="comment-author">{comment.created_by.name}</span>
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleDateString()} at{' '}
                        {new Date(comment.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <a 
                      href={comment.task_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="task-link"
                    >
                      View Task
                    </a>
                  </div>
                  
                  <div className="comment-task">
                    <strong>Task:</strong> {comment.task_name}
                    {comment.task_assignee && (
                      <span className="task-assignee">
                        (assigned to {comment.task_assignee})
                      </span>
                    )}
                  </div>
                  
                  <div className="comment-text">
                    {comment.text}
                  </div>
                </div>
              ))}
              
              <div className="search-stats">
                <p className="stats-note">
                  Found {unrepliedComments.length} pending comment{unrepliedComments.length !== 1 ? 's' : ''} 
                  {pendingComments.length > unrepliedComments.length && 
                    ` (${pendingComments.length - unrepliedComments.length} already replied)`
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="no-comments">
              <p>🎉 No pending comments found!</p>
              <p className="search-note">
                All comments and mentions have been addressed.
              </p>
            </div>
          )}
        </>
      )}

      {!commentsLoading && !commentsSearchCompleted && (
        <div className="no-comments">
          <p>Click "Refresh Comments" to search for pending comments and mentions.</p>
          <p className="loading-note">
            This will scan through all your projects and tasks to find comments that need your attention.
          </p>
        </div>
      )}
    </div>
  );
};

export default CommentsView; 